import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  faXmarkCircle,
  faCheckCircle,
  faPaperPlane,
  faHistory,
  faFolderOpen,
  faTrashAlt,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation } from "react-router-dom";
import { ModalBodylHistorial } from "../../historial-solicitudes/modalHistorial/modalHistorial";
import { Timeline } from "../../../components/ui/Timeline";
import Spinner from "../../../components/ui/Spinner";
import { modalEjecutar } from "../../../components/ui/sweetAlerts";
import { formatFecha, cierraModal } from "../../../utils/funciones";
import { PERFIL } from "../../../utils/Constants";
import { useSolicitudesCurso } from "./useSolicitudesCurso";
import { useTablaSolicitudes } from "../../tabla-solicitudes/useTablaSolicitudes";
import { ModalEditarSolicitudForm } from "../modalEditarSolicitud/ModalEditarSolicitud";

const LabelVal = ({ label, val }) => (
  <div className="mb-1 px-2">
    <div
      className="text-uppercase fw-bold"
      style={{ fontSize: "0.72rem", letterSpacing: "0.5px", color: "#64748b" }}
    >
      {label}
    </div>
    <div className="text-dark text-break" style={{ fontSize: "0.95rem", fontWeight: "500" }}>
      {val}
    </div>
  </div>
);

LabelVal.propTypes = {
  label: PropTypes.string,
  val: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const SectionTitle = ({ title }) => (
  <div className="mb-2 pb-1" style={{ borderBottom: "1px solid #e2e8f0" }}>
    <h6 className="fw-bold m-0" style={{ color: "#1e293b", fontSize: "0.9rem" }}>
      {title}
    </h6>
  </div>
);

SectionTitle.propTypes = {
  title: PropTypes.string,
};

const rtrnCambio = (tipo) => (tipo === 2 ? "Renovar plazo fijo" : "");
const rtrnTipoCambio = (tipo, fecha) =>
  tipo === 2 && fecha ? formatFecha(String(fecha).replaceAll("-", "")) : "";

const DetalleSolicitudSecciones = ({ obj, documentos, returnTipoContrato }) => {
  const nombreColaborador = `${obj.persona?.nombre || ""} ${obj.persona?.apellido_pat || ""} ${obj.persona?.apellido_mat || ""}`.trim();
  const cargoActual = obj.nombre_cargo_actual?.split("|")?.[1] || "---";
  const phtdActual = obj.nombre_cargo_actual?.split("|")?.[0] || "---";
  const phtdSol = obj.nuevo_plan_horario?.split("|")?.[0] || "---";
  const uoSol = obj.descripcion_unidad_temporis?.split(" - ")?.[0] || "---";
  const cargoSolTemporis = obj.descripcion_unidad_temporis?.split(" - ")?.[1] || "---";
  const planHorarioSol = obj.nuevo_plan_horario?.split("|")?.[1] || "---";

  return (
    <>
      <div>
        <SectionTitle title="Datos del colaborador" />
        <div className="row" style={{ rowGap: "7px" }}>
          <div className="col-6 col-md-6 col-lg-3">
            <LabelVal label="Nro personal" val={obj.persona?.id_sap || "No posee id SAP"} />
          </div>
          <div className="col-6 col-md-6 col-lg-3">
            <LabelVal label="Colaborador" val={nombreColaborador || "---"} />
          </div>
          <div className="col-6 col-md-6 col-lg-3">
            <LabelVal label="RUT" val={obj.persona?.identificacion || "---"} />
          </div>
          <div className="col-6 col-md-6 col-lg-3">
            <LabelVal label="Estatus contrato" val={returnTipoContrato(obj.persona?.tipo_contrato)} />
          </div>
        </div>
      </div>

      <div>
        <SectionTitle title="Datos asociados a SAP" />
        <div className="row" style={{ rowGap: "7px" }}>
          <div className="col-6 col-md-6 col-lg-3">
            <LabelVal label="Instalación actual" val={obj.instalacion_actual || "---"} />
          </div>
          {obj.id_tipo_solicitud === 3 && (
            <div className="col-6 col-md-6 col-lg-3">
              <LabelVal label="Instalación a traspasar" val={obj.nombre_instalacion_destino || "---"} />
            </div>
          )}
          <div className="col-6 col-md-6 col-lg-3">
            <LabelVal label="Cargo actual" val={cargoActual} />
          </div>
          <div className="col-6 col-md-6 col-lg-3">
            <LabelVal label="Cargo solicitado" val={obj.nombre_nuevo_cargo || "---"} />
          </div>
          <div className="col-6 col-md-6 col-lg-3">
            <LabelVal label="PHTD actual" val={phtdActual} />
          </div>
          <div className="col-6 col-md-6 col-lg-3">
            <LabelVal label="PHTD solicitado" val={phtdSol} />
          </div>
        </div>
      </div>

      {![3, 4, 5].includes(obj.id_tipo_solicitud) && (
        <div>
          <SectionTitle title="Datos asociados a temporis" />
          <div className="row" style={{ rowGap: "7px" }}>
            <div className="col-6 col-md-6 col-lg-3">
              <LabelVal label="Unidad solicitada" val={uoSol} />
            </div>
            <div className="col-6 col-md-6 col-lg-3">
              <LabelVal label="Cargo" val={cargoSolTemporis} />
            </div>
            <div className="col-6 col-md-6 col-lg-3">
              <LabelVal label="Plan horario" val={planHorarioSol} />
            </div>
          </div>
        </div>
      )}

      {obj.id_tipo_solicitud === 4 && (
        <div>
          <SectionTitle title="Datos complementarios" />
          <div className="row" style={{ rowGap: "7px" }}>
            <div className="col-6 col-md-6 col-lg-3">
              <LabelVal
                label="Estatus contrato"
                val={obj.tipo_cambio_contrato === 1 ? "Cambiar a indefinido" : rtrnCambio(obj.tipo_cambio_contrato)}
              />
            </div>
            <div className="col-6 col-md-6 col-lg-3">
              <LabelVal
                label="Fecha limite renovación"
                val={
                  obj.tipo_cambio_contrato === 1
                    ? "31-12-9999"
                    : rtrnTipoCambio(obj.tipo_cambio_contrato, obj.fecha_renovacion_contrato)
                }
              />
            </div>
          </div>
        </div>
      )}

      {obj.id_tipo_solicitud === 5 && (
        <div>
          <SectionTitle title="Baja del colaborador" />
          <div className="row" style={{ rowGap: "7px" }}>
            <div className="col-6 col-md-6 col-lg-3">
              <LabelVal
                label="Fecha solicitud de baja"
                val={obj.fecha_inicio_cambio ? formatFecha(String(obj.fecha_inicio_cambio).replaceAll("-", "")) : "---"}
              />
            </div>
            <div className="col-6 col-md-6 col-lg-3">
              <LabelVal label="Causal invocada" val={obj.clausula_despido?.nombre || "No especifíca"} />
            </div>
            <div className="col-12 col-md-12 col-lg-6" title={obj.motivo_desvinculacion}>
              <LabelVal label="Motivo desvinculación" val={obj.motivo_desvinculacion || "---"} />
            </div>
          </div>
        </div>
      )}

      {obj.id_tipo_solicitud !== 5 && (
        <div>
          <SectionTitle title="Fecha efectiva del cambio" />
          <div className="row">
            <div className="col-12">
              <LabelVal
                label="Fecha efectiva del cambio"
                val={obj.fecha_inicio_cambio ? formatFecha(String(obj.fecha_inicio_cambio).replaceAll("-", "")) : "---"}
              />
            </div>
          </div>
        </div>
      )}

      <div>
        <SectionTitle title="Documentos adjuntos" />
        <div className="d-flex flex-wrap gap-2 mt-1">
          {documentos && documentos.length > 0 ? (
            documentos.map((el) => {
              const docNombre = el.key_documento?.split("/")?.[2] || "documento";
              return (
                <a
                  key={docNombre.replaceAll(" ", "")}
                  target="_blank"
                  rel="noreferrer"
                  href={el.url}
                  className="badge text-dark text-decoration-none px-3 py-2 fs-6 rounded-pill"
                  style={{ backgroundColor: "#b8d1ea" }}
                >
                  {docNombre}
                </a>
              );
            })
          ) : (
            <div
              className="d-flex align-items-center gap-2 text-muted px-3 py-2 bg-light rounded-pill"
              style={{ border: "1px dashed #cbd5e1", fontSize: "0.85rem" }}
            >
              <FontAwesomeIcon icon={faFolderOpen} /> Sin documentos adjuntos
            </div>
          )}
        </div>
      </div>
    </>
  );
};

DetalleSolicitudSecciones.propTypes = {
  obj: PropTypes.object.isRequired,
  documentos: PropTypes.array,
  returnTipoContrato: PropTypes.func.isRequired,
};

const BotonesAccion = ({
  obj,
  visibleBotones,
  isAdmin,
  cargandoEliminar,
  puedeEliminar,
  eliminarSolicitudInicial,
  aceptaSolicitud,
  rechazaSolicitud,
  onEditar,
  verHistorial,
}) => (
  <div className="mt-4 pt-3" style={{ borderTop: "1px solid #e2e8f0" }}>
    <div className="d-flex gap-3 justify-content-between align-items-center w-100">
      <div className="d-flex gap-3 align-items-center">
        {visibleBotones && (
          <>
            {obj.id_estado_solicitud === 4 ? (
              <button
                className="btn btn-success px-4 py-2 fw-bold rounded-pill d-flex align-items-center transition-all"
                title="Enviar a SAP"
                onClick={() => aceptaSolicitud({ ...obj, aprueba: true })}
              >
                <span className="me-2">Enviar a SAP</span> <FontAwesomeIcon icon={faPaperPlane} />
              </button>
            ) : (
              <div className="d-flex flex-row gap-2">
                <button
                  className="btn btn-success px-4 py-2 fw-bold rounded-pill d-flex align-items-center transition-all"
                  title="Aprobar"
                  onClick={() => aceptaSolicitud({ ...obj, aprueba: true })}
                >
                  <span className="me-2">Aprobar</span> <FontAwesomeIcon icon={faCheckCircle} />
                </button>
                <button
                  className="btn btn-danger px-4 py-2 fw-bold rounded-pill d-flex align-items-center transition-all"
                  title="Rechazar"
                  onClick={() => {
                    cierraModal();
                    rechazaSolicitud({ ...obj, aprueba: false });
                  }}
                >
                  <span className="me-2">Rechazar</span> <FontAwesomeIcon icon={faXmarkCircle} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="d-flex gap-3 align-items-center ms-auto">
        {isAdmin && (
          <>
            {cargandoEliminar ? (
              <div style={{ width: "134px" }}>
                <Spinner clases="my-1" />
              </div>
            ) : (
              puedeEliminar && (
                <button
                  className="btn btn-danger px-4 py-2 fw-bold rounded-pill d-flex align-items-center transition-all"
                  title="Eliminar"
                  onClick={() => {
                    modalEjecutar(
                      eliminarSolicitudInicial,
                      "¿Está seguro de eliminar la solicitud?, Esta acción no se puede deshacer",
                      obj.id
                    );
                  }}
                >
                  <span className="me-2">Eliminar</span> <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              )
            )}
            {String(localStorage.getItem("id_perfil")) === PERFIL.RRHH && visibleBotones && (
              <button
                className="btn px-4 py-2 fw-bold rounded-pill d-flex align-items-center transition-all"
                style={{ color: "#ffffff", backgroundColor: "#f59e0b", border: "none" }}
                title="Editar datos de solicitud"
                onClick={onEditar}
              >
                <span className="me-2">Editar</span> <FontAwesomeIcon icon={faEdit} />
              </button>
            )}
          </>
        )}
        <button
          className="btn btn-light px-4 py-2 fw-bold rounded-pill d-flex align-items-center transition-all"
          style={{ color: "#2a77b9", border: "1px solid #cbd5e1" }}
          title="Ver historial"
          onClick={verHistorial}
        >
          <span className="me-2">Historial</span> <FontAwesomeIcon icon={faHistory} />
        </button>
      </div>
    </div>
  </div>
);

BotonesAccion.propTypes = {
  obj: PropTypes.object.isRequired,
  visibleBotones: PropTypes.bool,
  isAdmin: PropTypes.bool,
  cargandoEliminar: PropTypes.bool,
  puedeEliminar: PropTypes.bool,
  eliminarSolicitudInicial: PropTypes.func,
  aceptaSolicitud: PropTypes.func.isRequired,
  rechazaSolicitud: PropTypes.func.isRequired,
  onEditar: PropTypes.func,
  verHistorial: PropTypes.func.isRequired,
};

export const ModalVerSolicitud = ({ obj, visibleBtns, isAdmin = false }) => {
  const [visibleBotones, setVisibleBotones] = useState(false);
  const [cargandoEliminar, setCargandoEliminar] = useState(Boolean(isAdmin));
  const location = useLocation();

  const {
    returnAccesos,
    visibleHistorial,
    idSolicitud,
    setVisibleHistorial,
    traerDocumentos,
    documentos,
    setIdSolicitud,
    visibleEditarModal,
    setVisibleEditarModal,
    solicitudEditar,
    setSolicitudEditar,
    aceptaSolicitud,
    rechazaSolicitud,
    returnTipoContrato,
  } = useSolicitudesCurso();

  const { verPermiso, puedeEliminar, eliminarSolicitudInicial } = useTablaSolicitudes();

  useEffect(() => {
    if (isAdmin && [true, false].includes(puedeEliminar)) {
      setCargandoEliminar(false);
    }
  }, [isAdmin, puedeEliminar]);

  const validarBotones = (path) => {
    if (path === "/solicitudes-traspaso") {
      setVisibleBotones(Boolean(visibleBtns));
    } else {
      const resp = returnAccesos(
        localStorage.getItem("id_perfil"),
        obj?.id_tipo_solicitud,
        obj?.id_estado_solicitud
      );
      setVisibleBotones(Boolean(resp));
    }
  };

  useEffect(() => {
    if (obj?.id) {
      traerDocumentos(obj);
      validarBotones(location.pathname);
      if (isAdmin) {
        verPermiso(obj.id);
      }
    }
  }, [obj?.id, isAdmin]);

  if (!obj) {
    return <div className="text-center p-5 text-muted">No hay información disponible</div>;
  }

  if (visibleHistorial) {
    if (isAdmin) {
      return (
        <Timeline
          id={idSolicitud || obj.id}
          volver={() => setVisibleHistorial(false)}
        />
      );
    }
    return (
      <ModalBodylHistorial
        fnAtras={() => setVisibleHistorial(false)}
        id={idSolicitud || obj.id}
      />
    );
  }

  if (visibleEditarModal) {
    return (
      <ModalEditarSolicitudForm
        solicitud={solicitudEditar || obj}
        hideModal={() => setVisibleEditarModal(false)}
        onSuccess={() => {
          setVisibleEditarModal(false);
          cierraModal();
        }}
      />
    );
  }

  return (
    <div className="pb-2 cuerpoModalData d-flex flex-column gap-2 px-2">
      <DetalleSolicitudSecciones
        obj={obj}
        documentos={documentos}
        returnTipoContrato={returnTipoContrato}
      />
      <BotonesAccion
        obj={obj}
        visibleBotones={visibleBotones}
        isAdmin={isAdmin}
        cargandoEliminar={cargandoEliminar}
        puedeEliminar={puedeEliminar}
        eliminarSolicitudInicial={eliminarSolicitudInicial}
        aceptaSolicitud={aceptaSolicitud}
        rechazaSolicitud={rechazaSolicitud}
        onEditar={() => {
          setSolicitudEditar(obj);
          setVisibleEditarModal(true);
        }}
        verHistorial={() => {
          setIdSolicitud(obj.id);
          setVisibleHistorial(true);
        }}
      />
    </div>
  );
};

ModalVerSolicitud.propTypes = {
  obj: PropTypes.object,
  visibleBtns: PropTypes.bool,
  isAdmin: PropTypes.bool,
};
